import React, { useState, useCallback } from "react";
import styled, { createGlobalStyle } from "styled-components"; // ★ createGlobalStyle 추가

import ThemeContext from "./context/ThemeContext";
import LoginDialog from "./components/LoginDialog";
import ShopPage from "./components/ShopPage";
import CartPage from "./components/CartPage";
import BookDetail from "./components/BookDetail";
import Icons from "./components/Icons";

// --- ★ [핵심] 전역 스타일 정의 (body 배경색 제어) ---
const GlobalStyle = createGlobalStyle`
    body {
        margin: 0;
        padding: 0;
        /* body 태그 자체의 색상을 테마에 맞춰 변경 */
        background-color: ${(props) => (props.theme === "light" ? "#f9fafb" : "#111827")};
        color: ${(props) => (props.theme === "light" ? "#333" : "#f9fafb")};
        font-family: 'Noto Sans KR', sans-serif;
        transition: background-color 0.3s, color 0.3s;
    }
    
    * {
        box-sizing: border-box;
    }
`;

// --- Styled Components ---

const AppWrapper = styled.div`
    min-height: 100vh;
    /* AppWrapper는 투명하게 두거나 배경색을 상속받도록 설정 */
    background-color: transparent;
`;

const Navbar = styled.nav`
    background-color: ${(props) => (props.theme === "light" ? "white" : "#1f2937")};
    border-bottom: 1px solid ${(props) => (props.theme === "light" ? "#e5e7eb" : "#374151")};
    position: sticky;
    top: 0;
    z-index: 100;
    padding: 1rem 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: background-color 0.3s, border-color 0.3s;
`;

const NavContent = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Logo = styled.div`
    font-size: 1.25rem;
    font-weight: 800;
    color: ${(props) => (props.theme === "light" ? "#2563eb" : "#60a5fa")};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const NavLinks = styled.div`
    display: flex;
    gap: 1rem;
    align-items: center;
`;

const NavButton = styled.button`
    background: none;
    border: none;
    font-size: 0.9rem;
    font-weight: 600;
    color: ${(props) => (props.theme === "light" ? "#6b7280" : "#9ca3af")};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.8rem;
    border-radius: 0.5rem;
    transition: all 0.2s;

    &:hover {
        color: ${(props) => (props.theme === "light" ? "#2563eb" : "#60a5fa")};
        background-color: ${(props) => (props.theme === "light" ? "#eff6ff" : "#374151")};
    }
`;

const Badge = styled.span`
    background-color: #ef4444;
    color: white;
    font-size: 0.7rem;
    font-weight: bold;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding-left: 1rem;
    border-left: 1px solid #e5e7eb;
    margin-left: 0.5rem;
`;

// --- Main Component ---

function BookStoreApp() {
    const [theme, setTheme] = useState("light");
    const [user, setUser] = useState(null);
    const [page, setPage] = useState("shop");
    const [cart, setCart] = useState([]);
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [selectedBook, setSelectedBook] = useState(null);
    const [unit, setUnit] = useState("KRW");

    // Helper Functions
    const convertPrice = (priceInWon) => {
        if (unit === "KRW") return priceInWon.toLocaleString() + "원";
        return "$" + (priceInWon / 1500).toFixed(2);
    };

    const toggleUnit = () => setUnit(prev => prev === "KRW" ? "USD" : "KRW");
    const toggleTheme = useCallback(() => setTheme(prev => prev === "light" ? "dark" : "light"), []);
    const handleLogin = (email) => setUser({ email, name: email.split("@")[0] });
    const handleLogout = () => {
        if(window.confirm("로그아웃 하시겠습니까?")){
            setUser(null); setCart([]); setPage("shop");
        }
    };

    const addToCart = (book) => {
        if (!user) { setIsLoginOpen(true); return; }
        setCart([...cart, book]);
        if (window.confirm(`${book.title}을(를) 장바구니에 담았습니다.`)) {
            // 그대로 쇼핑 계속하기
        }
    };

    const handleViewDetail = (book) => {
        setSelectedBook(book);
        setPage("detail");
        window.scrollTo(0, 0);
    };

    const renderContent = () => {
        switch (page) {
            case "shop":
                return <ShopPage theme={theme} addToCart={addToCart} onViewDetail={handleViewDetail} convertPrice={convertPrice} />;
            case "cart":
                return <CartPage cartItems={cart} removeFromCart={(idx)=>setCart(cart.filter((_,i)=>i!==idx))} onOrder={()=>{alert("주문 완료!"); setCart([]); setPage("shop");}} convertPrice={convertPrice} theme={theme} />;
            case "detail":
                return <BookDetail book={selectedBook} theme={theme} onAddToCart={addToCart} onBack={() => setPage("shop")} convertPrice={convertPrice} />;
            default: return null;
        }
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {/* ★ GlobalStyle 컴포넌트 추가: body 태그 자체의 스타일을 변경함 */}
            <GlobalStyle theme={theme} />

            <AppWrapper>
                <Navbar theme={theme}>
                    <NavContent>
                        <Logo theme={theme} onClick={() => setPage("shop")}>
                            📚 송우네 책방
                        </Logo>
                        <NavLinks>
                            <NavButton theme={theme} onClick={toggleUnit}>
                                <Icons.Dollar />
                                <span>{unit === "KRW" ? "원화" : "USD"}</span>
                            </NavButton>

                            <NavButton theme={theme} onClick={toggleTheme}>
                                {theme === "light" ? <Icons.Moon /> : <Icons.Sun />}
                                <span>{theme === "light" ? "다크 모드" : "라이트 모드"}</span>
                            </NavButton>

                            <NavButton theme={theme} onClick={() => setPage("shop")}>
                                도서 목록
                            </NavButton>

                            <NavButton theme={theme} onClick={() => user ? setPage("cart") : setIsLoginOpen(true)}>
                                <Icons.Cart />
                                <span>장바구니</span>
                                {cart.length > 0 && <Badge>{cart.length}</Badge>}
                            </NavButton>

                            <UserInfo>
                                {user ? (
                                    <>
                                        <span style={{fontSize: "0.9rem", fontWeight:"bold"}}>{user.name}님</span>
                                        <NavButton theme={theme} onClick={handleLogout} style={{color:"#ef4444"}}>로그아웃</NavButton>
                                    </>
                                ) : (
                                    <NavButton theme={theme} onClick={() => setIsLoginOpen(true)} style={{color:"#2563eb", fontWeight:"bold"}}>
                                        로그인
                                    </NavButton>
                                )}
                            </UserInfo>
                        </NavLinks>
                    </NavContent>
                </Navbar>

                <main>
                    {renderContent()}
                </main>

                <LoginDialog isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} onLogin={handleLogin} />
            </AppWrapper>
        </ThemeContext.Provider>
    );
}

export default BookStoreApp;